import React, { useState, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import Text from '@/common/view/Text';
import Icon from '@/common/view/icons';
import Spinner from '@/common/view/Spinner';
import { markdownBaseStyles } from '@/dashboard/customer-analysis/view/styles/MarkdownStyles';
import { useCustomerDetailPageContext } from '../datacontroller/CustomerDetailPageContext';
import CustomerApiAgent from '../../main/agent/CustomerApiAgent';
import Markdown from 'markdown-to-jsx';

/**
 * AI 응대 멘트 생성 실패 시 기본 폴백 문구 생성
 */
const generateFallbackScript = (customerName?: string): string => {
  const name = customerName || '고객';

  return `**👋 인사**

**"${name}님, 반갑습니다!"**

**💬 대화**

**"오늘 찾으시는 상품이 있으신가요?"**

**"필요하신 것이 있으시면 말씀해 주세요."**

**"편하게 둘러보시고 도움이 필요하시면 불러주세요."**`;
};

// 깜빡이는 커서 애니메이션
const blink = keyframes`
  0%, 50% {
    opacity: 1;
  }
  51%, 100% {
    opacity: 0;
  }
`;

// Styled Components
const Container = styled.div`
  background: ${({ theme }) => theme.colors.gray10};
  border-radius: 8px;
  padding: 20px;
  overflow: hidden;
`;

const ScriptBox = styled.div`
  background: ${({ theme }) => theme.colors.grayWhite};
  border-radius: 8px;
  padding: 20px;
  min-height: 100px;
`;

const Header = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  cursor: pointer;
  user-select: none;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ExpandIcon = styled.div<{ $isExpanded: boolean }>`
  transform: ${({ $isExpanded }) => ($isExpanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease;
  display: flex;
  align-items: center;
`;

const Content = styled.div<{ $isExpanded: boolean }>`
  display: ${({ $isExpanded }) => ($isExpanded ? 'block' : 'none')};
`;

const ErrorBox = styled.div`
  background: #fef2f2;
  border-radius: 6px;
  padding: 20px;
  text-align: center;
  color: ${({ theme }) => theme.colors.gray100};
`;

const MarkdownContent = styled.div`
  ${markdownBaseStyles}

  color: ${({ theme }) => theme.colors.gray100};

  /* 제목 색상 */
  h4 { color: ${({ theme }) => theme.colors.gray100}; }
  h5 { color: ${({ theme }) => theme.colors.gray50}; }
  h6 { color: ${({ theme }) => theme.colors.gray40}; }

  /* ul 리스트 - 회색 불릿 */
  ul li::before {
    background: ${({ theme }) => theme.colors.gray50};
  }

  ul li ul li::before {
    border: 2px solid ${({ theme }) => theme.colors.gray30};
  }

  /* ol 리스트 - 단순 숫자+점 스타일 (배지 아님) */
  ol > li::before {
    content: counter(list-counter) '.';
    color: ${({ theme }) => theme.colors.gray100};
  }

  ol ol > li::before {
    content: counter(list-counter-nested) '.';
    color: ${({ theme }) => theme.colors.gray50};
  }

  /* hr 스타일 */
  hr {
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => theme.colors.gray30} 20%,
      ${({ theme }) => theme.colors.gray30} 80%,
      transparent
    );
  }

  /* 코드 스타일 */
  code {
    background: ${({ theme }) => theme.colors.gray20};
  }

  pre {
    background: ${({ theme }) => theme.colors.gray100};

    code {
      color: ${({ theme }) => theme.colors.gray10};
    }
  }

  /* 강조 스타일 */
  strong { color: ${({ theme }) => theme.colors.gray100}; }
  em { color: ${({ theme }) => theme.colors.gray50}; }

  /* 링크 스타일 */
  a { color: ${({ theme }) => theme.colors.gray100}; }
`;

// 스트리밍 중 깜빡이는 커서
const StreamingCursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 14px;
  background: ${({ theme }) => theme.colors.gray50};
  margin-left: 2px;
  vertical-align: text-bottom;
  animation: ${blink} 1s step-end infinite;
`;

const LoadingContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CharCount = styled.div`
  margin-top: 12px;
  text-align: right;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray50};
`;

const FallbackBadge = styled.span`
  display: inline-block;
  padding: 2px 6px;
  margin-right: 8px;
  background: ${({ theme }) => theme.colors.gray20};
  border-radius: 4px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.gray50};
`;

// 메인 컴포넌트
export const CustomerAIGuideSection: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const { customerId, customerDetail } = useCustomerDetailPageContext();

  const {
    data,
    streamingText,
    isStreaming,
    isConnecting,
    error,
  } = CustomerApiAgent.useGetCustomerProfileStream(customerId);

  const toggleExpand = () => setIsExpanded(!isExpanded);

  // 폴백 문구 생성 (고객 이름 기반)
  const fallbackScript = useMemo(
    () => generateFallbackScript(customerDetail?.customer_name),
    [customerDetail?.customer_name]
  );

  // 에러 발생 시 폴백 문구 사용
  const scriptContent = isStreaming
    ? streamingText
    : (data?.script || (error ? fallbackScript : ''));
  const isLoading = isConnecting || isStreaming;
  const isUsingFallback = !isStreaming && !data?.script && !!error;

  return (
    <Container>
      <Header $isExpanded={isExpanded} onClick={toggleExpand}>
        <Title>
          <Text typo="subtitle2" color="grayBlack">
            AI 기반 응대 멘트 추천
          </Text>
        </Title>
        <ExpandIcon $isExpanded={isExpanded}>
          <Icon.ArrowDown width={20} height={20} />
        </ExpandIcon>
      </Header>

      <Content $isExpanded={isExpanded}>
        {(scriptContent || isLoading) && (
          <>
            <ScriptBox>
              {isLoading && !scriptContent && (
                <LoadingContent>
                  <Spinner size="xsmall" />
                  <Text typo="body4" color="gray40">
                    고객 정보를 분석하여 맞춤 응대 멘트를 생성하고 있습니다...
                  </Text>
                </LoadingContent>
              )}
              {scriptContent && (
                <MarkdownContent>
                  <Markdown>{scriptContent}</Markdown>
                  {isStreaming && <StreamingCursor />}
                </MarkdownContent>
              )}
            </ScriptBox>
            {isUsingFallback && scriptContent && (
              <CharCount>
                <FallbackBadge>기본 멘트</FallbackBadge>
              </CharCount>
            )}
          </>
        )}
        
        {data && !scriptContent && !error && (
          <ErrorBox>
            <Text typo="body4" color="gray50">생성된 응대 스크립트가 없습니다.</Text>
          </ErrorBox>
        )}
      </Content>
    </Container>
  );
};


